import React, { useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import MovieFilterRoundedIcon from '@mui/icons-material/MovieFilterRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import UserPanelCategoryCarousel from './UserPanelCategoryCarousel';
import UserPanelContentSkeleton from './UserPanelContentSkeleton';
import usePosterImageState from './hooks/usePosterImageState';
import { matchesTurkishSearch, normalizeTurkishSearchText } from './utils/turkishSearch';
import { MOVIE_CATEGORIES } from './userPanelMoviesData';
import {
  formatMovieCount,
  getMovieCategoryGroupId,
  isSyntheticMovieCategoryName,
  selectDailyPopularMovies,
  selectRecentlyAddedMovies,
  sortMovieCategoriesByServerOrder,
  sortMoviesNewestFirst,
} from './userPanelMovieOrdering';
import './userPanelMovies.css';

const MOVIE_BATCH_SIZE = 30;
const KNOWN_CATEGORY_MAP = new Map(MOVIE_CATEGORIES.map((category) => [category.id, category]));

const getCommentCount = (item) => {
  const value = item.yorum_sayisi ?? item.commentCount ?? item.comments?.length ?? 0;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? Math.floor(numericValue) : 0;
};

function MoviePoster({ item }) {
  const posterState = usePosterImageState(item.poster);
  const hasPoster = !posterState.isFailed;

  return (
    <span
      ref={posterState.hostRef}
      className={`tvup-movies-library-poster${item.posterFit === 'contain' ? ' is-contain' : ''}${hasPoster ? ' has-poster' : ''}${posterState.isLoaded ? ' is-loaded' : ' is-loading'}`}
      style={{ '--tvup-movies-card-rgb': item.categoryRgb }}
      aria-busy={posterState.isLoading ? 'true' : undefined}
    >
      {hasPoster && posterState.shouldRequest ? (
        <img
          src={posterState.posterSource}
          alt=""
          loading="eager"
          decoding="async"
          referrerPolicy="no-referrer"
          onLoad={posterState.handleLoad}
          onError={posterState.handleError}
        />
      ) : !hasPoster ? (
        <span className="tvup-movies-poster-fallback" aria-hidden="true">
          <MovieFilterRoundedIcon />
        </span>
      ) : null}
    </span>
  );
}

function MovieCard({ item, onSelect, onComments }) {
  const interactive = typeof onSelect === 'function';
  const commentsInteractive = typeof onComments === 'function';

  return (
    <article className={`tvup-movies-card${interactive || commentsInteractive ? ' is-interactive' : ''}`}>
      {interactive && (
        <button
          type="button"
          className="tvup-movies-card-open-hit"
          onClick={() => onSelect(item.raw || item)}
          aria-label={`${item.name} film detaylarını aç`}
          aria-haspopup="dialog"
          aria-controls="tvup-content-detail-modal"
        />
      )}
      <MoviePoster item={item} />
      <span className="tvup-movies-card-copy">
        <b title={item.name}>{item.name}</b>
        <span className="tvup-movies-card-engagement">
          <span className="tvup-movies-rating" aria-label={`${item.rating || 'Puan yok'} puan`}>
            <StarRoundedIcon aria-hidden="true" />
            <b>{item.rating || '—'}</b>
          </span>
          {commentsInteractive ? (
            <button
              type="button"
              className="tvup-movies-comments-button"
              onClick={() => onComments(item)}
              aria-label={`${item.name} filmi için ${item.commentCount} yorumu aç`}
              aria-haspopup="dialog"
              aria-controls="tvup-comments-modal"
            >
              <ChatBubbleOutlineRoundedIcon aria-hidden="true" />
              {item.commentCount} Yorum
            </button>
          ) : (
            <span className="tvup-movies-comments-count">
              <ChatBubbleOutlineRoundedIcon aria-hidden="true" />
              {item.commentCount} Yorum
            </span>
          )}
        </span>
      </span>
    </article>
  );
}

function UserPanelMovies({ movies = [], loading = false, onMovieSelect, onMovieComments }) {
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(MOVIE_BATCH_SIZE);

  const normalizedMovies = useMemo(() => movies.map((item, index) => {
    const categoryId = String(item.categoryId || item.category_id || 'movies');
    const knownCategory = KNOWN_CATEGORY_MAP.get(categoryId);
    const categoryName = item.categoryName
      || item.genre
      || item.category_name
      || knownCategory?.name
      || 'Diğer Filmler';
    const categoryGroupId = getMovieCategoryGroupId(categoryName, categoryId);

    return {
      raw: item,
      id: String(item.id || item.stream_id || `${categoryId}-${index}`),
      name: String(item.name || item.title || 'İsimsiz film'),
      poster: item.poster || item.stream_icon || '',
      year: item.year || item.release_year || '',
      rating: item.imdbRating || item.imdb_rating || item.rating || '',
      commentCount: getCommentCount(item),
      added: item.added || 0,
      stream_id: item.stream_id || item.id,
      recentlyAddedRank: item.recentlyAddedRank ?? null,
      genre: item.genre || item.category_name || categoryName,
      categoryId,
      categoryGroupId,
      categoryName,
      categoryKnown: item.categoryKnown !== false,
      categoryOrder: item.categoryOrder,
      categoryColor: item.categoryColor || knownCategory?.color || '#ff8a1f',
      categoryRgb: item.categoryRgb || knownCategory?.rgb || '255, 138, 31',
      posterFit: item.posterFit || 'cover',
    };
  }).filter((item) => Boolean(item.poster)), [movies]);

  const categoryOptions = useMemo(() => {
    const optionsById = new Map();

    normalizedMovies.forEach((item, serverIndex) => {
      if (!item.categoryKnown) return;
      const existingOption = optionsById.get(item.categoryGroupId);
      if (existingOption) {
        existingOption.count += 1;
        existingOption.categoryOrder = Math.min(
          Number(existingOption.categoryOrder ?? Number.MAX_SAFE_INTEGER),
          Number(item.categoryOrder ?? Number.MAX_SAFE_INTEGER)
        );
        return;
      }

      optionsById.set(item.categoryGroupId, {
        id: item.categoryGroupId,
        name: item.categoryName,
        color: item.categoryColor,
        rgb: item.categoryRgb,
        image: item.poster,
        count: 1,
        categoryOrder: item.categoryOrder,
        serverIndex,
      });
    });

    return sortMovieCategoriesByServerOrder([...optionsById.values()])
      .filter((category) => !isSyntheticMovieCategoryName(category.name));
  }, [normalizedMovies]);

  const recentlyAddedMovies = useMemo(
    () => selectRecentlyAddedMovies(normalizedMovies),
    [normalizedMovies]
  );

  const popularMovies = useMemo(
    () => selectDailyPopularMovies(normalizedMovies),
    [normalizedMovies]
  );

  const categoryTabs = useMemo(() => ([
    {
      id: 'all',
      name: 'Yeni Eklenenler',
      color: '#ff8a1f',
      rgb: '255, 138, 31',
      image: null,
      count: recentlyAddedMovies.length,
    },
    {
      id: 'popular',
      name: 'Bugünün Popüler',
      color: '#ff8a1f',
      rgb: '255, 138, 31',
      image: popularMovies[0]?.poster || null,
      count: popularMovies.length,
    },
    ...categoryOptions,
  ]), [categoryOptions, popularMovies, recentlyAddedMovies.length]);

  const filteredMovies = useMemo(() => {
    const normalizedQuery = normalizeTurkishSearchText(query);
    const categoryMovies = normalizedQuery
      ? sortMoviesNewestFirst(normalizedMovies)
      : activeCategory === 'all'
        ? recentlyAddedMovies
        : activeCategory === 'popular'
          ? popularMovies
          : sortMoviesNewestFirst(
            normalizedMovies.filter((item) => item.categoryGroupId === activeCategory)
          );

    return categoryMovies.filter((item) => {
      const searchableText = `${item.name} ${item.genre} ${item.year}`;
      return matchesTurkishSearch(searchableText, normalizedQuery);
    });
  }, [activeCategory, normalizedMovies, popularMovies, query, recentlyAddedMovies]);

  const displayedMovies = useMemo(
    () => filteredMovies.slice(0, visibleCount),
    [filteredMovies, visibleCount]
  );

  const activeCategoryLabel = categoryTabs.find((category) => category.id === activeCategory)?.name || 'Yeni Eklenenler';
  const hasActiveSearch = query.trim().length > 0;

  useEffect(() => {
    setVisibleCount(MOVIE_BATCH_SIZE);
  }, [activeCategory, query]);

  useEffect(() => {
    if (!['all', 'popular'].includes(activeCategory)
      && !categoryOptions.some((category) => category.id === activeCategory)) {
      setActiveCategory('all');
    }
  }, [activeCategory, categoryOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const consumeHeaderSearch = () => {
      try {
        const storedSearch = window.sessionStorage.getItem('filmSearchQuery');
        if (!storedSearch) return;
        window.sessionStorage.removeItem('filmSearchQuery');

        let nextQuery = storedSearch;
        try {
          const parsedSearch = JSON.parse(storedSearch);
          nextQuery = parsedSearch?.query || '';
        } catch (error) {
          // Eski düz metin formatı da desteklenir.
        }

        if (nextQuery) {
          setActiveCategory('all');
          setQuery(String(nextQuery));
        }
      } catch (error) {
        // Session storage kapalıysa sayfa içi arama çalışmaya devam eder.
      }
    };

    consumeHeaderSearch();
    window.addEventListener('filmSearchUpdated', consumeHeaderSearch);
    return () => window.removeEventListener('filmSearchUpdated', consumeHeaderSearch);
  }, []);

  const selectCategory = (categoryId) => {
    setActiveCategory(categoryId);
    setQuery('');
    setVisibleCount(MOVIE_BATCH_SIZE);
  };

  const handleCategoryKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const lastIndex = categoryTabs.length - 1;
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? lastIndex
        : event.key === 'ArrowRight'
          ? (index + 1) % categoryTabs.length
          : (index - 1 + categoryTabs.length) % categoryTabs.length;

    selectCategory(categoryTabs[nextIndex].id);
    const buttons = event.currentTarget.parentElement?.querySelectorAll('[role="tab"]');
    buttons?.[nextIndex]?.focus();
  };

  if (loading && normalizedMovies.length === 0) {
    return <UserPanelContentSkeleton variant="movies" />;
  }

  return (
    <div className="tvup-movies-page">
      <section className="tvup-movies-category-section" aria-labelledby="tvup-movies-category-title">
        <div className="tvup-movies-section-heading">
          <h2 id="tvup-movies-category-title">Bugün Hangi Filmi İzlemek İstersin?</h2>
          <p>Türüne göre keşfet veya aradığın filmi bul.</p>
        </div>

        <UserPanelCategoryCarousel
          id="tvup-movies-category-tabs"
          className="tvup-movies-categories"
          ariaLabel="Film kategorileri"
          previousLabel="Film kategorilerini sola kaydır"
          nextLabel="Film kategorilerini sağa kaydır"
        >
          {categoryTabs.map((category, index) => {
            const isActive = activeCategory === category.id;
            return (
              <button
                key={category.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="tvup-movies-library-panel"
                tabIndex={isActive ? 0 : -1}
                className={`${isActive ? 'is-active ' : ''}${category.id === 'all' ? 'is-all' : ''}`.trim()}
                style={{ '--tvup-movies-category-rgb': category.rgb }}
                onClick={() => selectCategory(category.id)}
                onKeyDown={(event) => handleCategoryKeyDown(event, index)}
              >
                {category.image && <img src={category.image} alt="" loading="lazy" referrerPolicy="no-referrer" />}
                <span className="tvup-movies-category-shade" />
                <span className="tvup-movies-category-copy">
                  <small>{category.name}</small>
                  <b>{formatMovieCount(category.count)} film</b>
                </span>
              </button>
            );
          })}
        </UserPanelCategoryCarousel>
      </section>

      <section
        id="tvup-movies-library-panel"
        className="tvup-movies-library"
        role="tabpanel"
        aria-labelledby="tvup-movies-category-title"
      >
        <div className="tvup-movies-toolbar">
          <div>
            <h2>{hasActiveSearch ? 'Film Arama Sonuçları' : activeCategory === 'all' ? 'Yeni Eklenen Filmler' : activeCategoryLabel}</h2>
            <p aria-live="polite">{formatMovieCount(filteredMovies.length)} film gösteriliyor</p>
          </div>
          <label className="tvup-movies-search">
            <SearchRoundedIcon aria-hidden="true" />
            <input
              type="search"
              value={query}
              aria-label="Film ara"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>

        {displayedMovies.length > 0 ? (
          <div className="tvup-movies-grid">
            {displayedMovies.map((item) => (
              <MovieCard
                key={item.id}
                item={item}
                onSelect={onMovieSelect}
                onComments={onMovieComments}
              />
            ))}
          </div>
        ) : (
          <div className="tvup-movies-empty" role="status">
            <MovieFilterRoundedIcon />
            <b>Bu aramayla eşleşen film bulunamadı.</b>
            <span>Farklı bir film adı deneyebilir veya kategoriyi değiştirebilirsin.</span>
          </div>
        )}

        {visibleCount < filteredMovies.length && (
          <button
            type="button"
            className="tvup-movies-load-more"
            onClick={() => setVisibleCount((current) => current + MOVIE_BATCH_SIZE)}
          >
            <AddRoundedIcon /> Daha fazla göster
            <small>{formatMovieCount(filteredMovies.length - visibleCount)} film kaldı</small>
          </button>
        )}
      </section>
    </div>
  );
}

export default UserPanelMovies;
